<template>
	<div v-loading="loading" class="main-content">
		<div class="main-content-inner">
			<div v-if="!loading" class="container-fluid">
				<div class="row">
					<div class="col-lg-12">
						<div class="heading">
							<h1>
								Analytics overview
							</h1>
							<div class="heading-date">
								<el-date-picker
									v-model="rangeValue"
									type="daterange"
									align="center"
									unlink-panels
									range-separator="-"
									format="dd-MM-yyyy"
									value-format="yyyy-MM-dd"
									start-placeholder="Start date"
									end-placeholder="End date"
									:picker-options="pickerOptions"
								/>
								<el-button size="big" :disabled="!rangeValue" @click="submitRange">Submit</el-button>
							</div>
							<div class="heading-mobile">
								<div class="margin-bottom-10">
									<el-date-picker
										v-model="rangeValue[0]"
										format="dd-MM-yyyy"
										value-format="yyyy-MM-dd"
										type="date"
										:picker-options="pickerMobileOptions"
										placeholder="Start date">
									</el-date-picker>
								</div>
								<div class="margin-bottom-10">
									<el-date-picker
										v-model="rangeValue[1]"
										format="dd-MM-yyyy"
										value-format="yyyy-MM-dd"
										:picker-options="pickerMobileOptions"
										type="date"
										placeholder="End date">
									</el-date-picker>
								</div>
								<el-button size="big" @click="submitRange">Submit</el-button>
							</div>
						</div>
					</div>
				</div>
				<div class="row stretch">
					<div class="col-lg-4 content-margin">
						<div class="content">
							<div v-loading="widgetLoading" class="content-body">
								<div class="content-body-statistics-overview-icon growth"></div>
								<div class="content-body-statistics-overview-title revenue">
									{{ analyticsData.total.revenue | convertCurrency }}
								</div>
								<div class="content-body-statistics-overview-subtitle">
									Revenue
								</div>
							</div>
						</div>
					</div>
					<div class="col-lg-4 content-margin">
						<div class="content">
							<div v-loading="widgetLoading" class="content-body">
								<div class="content-body-statistics-overview-icon decline"></div>
								<div class="content-body-statistics-overview-title cost">
									{{ analyticsData.total.cost | convertCurrency }}
								</div>
								<div class="content-body-statistics-overview-subtitle">
									Cost
								</div>
							</div>
						</div>
					</div>
					<div class="col-lg-4 content-margin">
						<div class="content">
							<div v-loading="widgetLoading" class="content-body">
								<div class="content-body-statistics-overview-icon profit"></div>
								<div class="content-body-statistics-overview-title profit">
									{{ analyticsData.total.profit | convertCurrency }}
								</div>
								<div class="content-body-statistics-overview-subtitle">
									Profit
								</div>
							</div>
						</div>
					</div>
				</div>
				<div class="row stretch">
					<div class="col-lg-12 content-margin">
						<div class="content">
							<div v-loading="widgetLoading" class="content-body">
								<h5 class="flex-between">
									Profit
								</h5>
								<GChart type="LineChart" :data="graphProfitData" :options="graphProfitOptions" />
							</div>
						</div>
					</div>
				</div>
				<div class="row stretch">
					<div class="col-lg-4 col-md-4 content-margin">
						<div class="content">
							<div v-loading="widgetLoading" class="content-body">
								<div class="content-body-statistics">
									<h5>
										Applications
									</h5>
									<div class="content-body-statistics-table">
										<div class="content-body-statistics-table-row">
											<span>Total</span>
											<strong>{{ analyticsData.total.accepted + analyticsData.total.rejected + analyticsData.total.pending + analyticsData.total.started + analyticsData.total.validation_error + analyticsData.total.duplicate }}</strong>
										</div>
										<div class="content-body-statistics-table-row">
											<span>Finished</span>
											<strong>{{ analyticsData.total.accepted + analyticsData.total.rejected + analyticsData.total.pending }}</strong>
										</div>
										<div class="content-body-statistics-table-row">
											<span>Unfinished</span>
											<strong>{{ analyticsData.total.started + analyticsData.total.validation_error }}</strong>
										</div>
										<div class="content-body-statistics-table-row">
											<span>Duplicate</span>
											<strong>{{ analyticsData.total.duplicate }}</strong>
										</div>
										<div class="content-body-statistics-table-row">
											<span>Pending</span>
											<strong>{{ analyticsData.total.pending }}</strong>
										</div>
										<div class="content-body-statistics-table-row">
											<span>Accepted</span>
											<strong>{{ analyticsData.total.accepted }}</strong>
										</div>
										<div class="content-body-statistics-table-row indent">
											<span>Standard accepted</span>
											<strong>{{ analyticsData.total.standard_accepted }}</strong>
										</div>
										<div class="content-body-statistics-table-row indent">
											<span>Low accepted</span>
											<strong>{{ analyticsData.total.low_accepted }}</strong>
										</div>
										<div class="content-body-statistics-table-row indent">
											<span>Rejected -> Accepted</span>
											<strong>{{ analyticsData.total.rejected_to_accepted }}</strong>
										</div>
										<div class="content-body-statistics-table-row">
											<span>Paid out</span>
											<strong>{{ analyticsData.total.paid_out }}</strong>
										</div>
										<div class="content-body-statistics-table-row">
											<span>Rejected</span>
											<strong>{{ analyticsData.total.rejected }}</strong>
										</div>
										<div class="content-body-statistics-table-row">
											<span>Server error</span>
											<strong>{{ analyticsData.total.server_error }}</strong>
										</div>
										<div class="content-body-statistics-table-row">
											<span>Validation error</span>
											<strong>{{ analyticsData.total.validation_error }}</strong>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
					<div class="col-lg-8 col-md-8 content-margin">
						<div class="content">
							<div v-loading="widgetLoading" class="content-body">
								<h5>
									Result
								</h5>
								<GChart type="PieChart" :data="graphStartedData" :options="graphStartedOptions" />
							</div>
						</div>
					</div>
				</div>
				<div class="row stretch">
					<div class="col-lg-6 content-margin">
						<div class="content">
							<div v-loading="widgetLoading" class="content-body">
								<h5>
									Status
								</h5>
								<GChart type="PieChart" :data="graphStatusData" :options="graphStatusOptions" />
							</div>
						</div>
					</div>
					<div class="col-lg-6 content-margin">
						<div class="content">
							<div v-loading="widgetLoading" class="content-body">
								<h5>
									Bounce
								</h5>
								<GChart type="PieChart" :data="graphBouncesData" :options="graphBouncesOptions" />
							</div>
						</div>
					</div>
				</div>
				<div v-if="Object.keys(analyticsData.by_source).length > 0" class="row">
					<div class="col-lg-12">
						<div class="heading">
							<h1>
								Traffic sources
							</h1>
						</div>
					</div>
				</div>
				<div v-if="Object.keys(analyticsData.by_source).length > 0" class="row stretch">
					<div v-for="(item, index) in analyticsData.by_source" :key="index" class="col-lg-4 col-md-6 content-margin">
						<div class="content">
							<div class="content-body">
								<div v-loading="widgetLoading" class="content-body-statistics">
									<h5>
										{{ index }}
									</h5>
									<div class="content-body-statistics-table">
										<div class="content-body-statistics-table-row">
											<span>Total</span>
											<strong>{{ item.started + item.accepted + item.rejected + item.pending }}</strong>
										</div>
										<div class="content-body-statistics-table-row">
											<span>Pending</span>
											<strong>{{ item.pending }}</strong>
										</div>
										<div class="content-body-statistics-table-row">
											<span>Finished</span>
											<strong>{{ item.accepted + item.rejected + item.pending }}</strong>
										</div>
										<div class="content-body-statistics-table-row">
											<span>Unfinished</span>
											<strong>{{ item.started }}</strong>
										</div>
										<div class="content-body-statistics-table-row">
											<span>Accepted</span>
											<strong>{{ item.accepted }}</strong>
										</div>
										<div class="content-body-statistics-table-row">
											<span>Rejected</span>
											<strong>{{ item.rejected }}</strong>
										</div>
										<div class="content-body-statistics-table-row">
											<span>Duplicate</span>
											<strong>{{ item.duplicate }}</strong>
										</div>
									</div>
									<div class="content-body-statistics-revenue">
										<div class="content-body-statistics-revenue-row revenue">
											<span>Revenue</span>
											<span>{{ item.revenue | convertCurrency }}</span>
										</div>
										<div class="content-body-statistics-revenue-row cost">
											<span>Cost</span>
											<span>{{ item.cost | convertCurrency }}</span>
										</div>
										<div class="content-body-statistics-revenue-row profit">
											<span>Profit</span>
											<span>{{ item.profit | convertCurrency }}</span>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { HTTP } from '@/services/api'
import { GChart } from 'vue-google-charts'

export default {
	name: 'Analytics',
	metaInfo () {
		return {
			title: 'Analytics',
		}
	},
	components: {
		GChart
	},
	data () {
        return {
			initial: false,
			loading: true,
			startDate: null,
			endDate: null,
			format: 'day',
			widgetLoading: false,
			rangeValue: null,
			interval: null,
			refreshTime: 60 * 1000,
			analyticsData: [],
			pickerOptions: {
				disabledDate(time) {
					return time.getTime() > Date.now();
				},
				shortcuts: [
					{
						text: 'Last week',
						onClick(picker) {
							const end = new Date()
							const start = new Date()
							start.setTime(start.getTime() - 3600 * 1000 * 24 * 7)
							picker.$emit('pick', [start, end])
						}
					},
					{
						text: 'Last 14 days',
						onClick(picker) {
							const end = new Date()
							const start = new Date()
							start.setTime(start.getTime() - 3600 * 1000 * 24 * 14)
							picker.$emit('pick', [start, end])
						}
					},
					{
						text: 'Last month',
						onClick(picker) {
							const end = new Date()
							const start = new Date()
							start.setTime(start.getTime() - 3600 * 1000 * 24 * 30)
							picker.$emit('pick', [start, end])
						}
					},
					{
						text: 'Last 3 months',
						onClick(picker) {
							const end = new Date()
							const start = new Date()
							start.setTime(start.getTime() - 3600 * 1000 * 24 * 90)
							picker.$emit('pick', [start, end])
						}
					},
					{
						text: 'Last 6 months',
						onClick(picker) {
							const end = new Date()
							const start = new Date()
							start.setTime(start.getTime() - 3600 * 1000 * 24 * 180)
							picker.$emit('pick', [start, end])
						}
					},
					{
						text: 'Last 12 months',
						onClick(picker) {
							const end = new Date()
							const start = new Date()
							start.setTime(start.getTime() - 3600 * 1000 * 24 * 365)
							picker.$emit('pick', [start, end])
						}
					}
				]
			},
			pickerMobileOptions: {
				disabledDate(time) {
					return time.getTime() > Date.now();
				}
			},
			graphProfitData: [],
			graphProfitOptions: {
				height: 350,
				colors: ['#3366cc','#dc3912', '#67b018'],
				legend: 'top',
				pointSize: 5,
				curveType: 'function',
				chartArea: {
					'height': '80%',
					'width': '90%'
				},
			},
			graphBouncesData: [],
			graphBouncesOptions: {
				height: 300,
				pieHole: 0.5,
				legend: {
					position: 'labeled',
					labeledValueText: 'both'
				},
				colors: ['#abc1ea', '#83a2e0', '#5a84d6'],
				pieSliceText: 'none',
				chartArea: {
					height: 200,
					width: '100%'
				},
				tooltip: {
					trigger: 'selection'
				}
			},
			graphStatusData: [],
			graphStatusOptions: {
				height: 240,
				pieHole: 0.5,
				legend: {
					position: 'labeled',
					labeledValueText: 'both'
				},
				colors: ['#67b018', '#dc3912', '#83a2e0', '#ffbe00', '#ce18ae', '#d113dc', '#d113dc'],
				pieSliceText: 'none',
				chartArea: {
					height: 200,
					width: '100%'
				},
				tooltip: {
					trigger: 'selection'
				}
			},
			graphStartedData: [],
			graphStartedOptions: {
				height: 240,
				pieHole: 0.5,
				legend: {
					position: 'labeled',
					labeledValueText: 'both'
				},
				colors: ['#67b018', '#dc3912'],
				pieSliceText: 'none',
				chartArea: {
					height: 200,
					width: '100%'
				},
				tooltip: {
					trigger: 'selection'
				}
			},
        }
    },
	mounted () {
		this.startDate = this.formatDate(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000))
		this.endDate = this.formatDate(new Date(Date.now()))
		this.loadStatistics()
		this.interval = setInterval(() => { this.loadStatistics() }, this.refreshTime)
		this.rangeValue = [this.startDate, this.endDate]
	},
	destroyed () {
		clearInterval(this.interval)
	},
	methods: {
		loadStatistics () {
			if (!this.initial) {
				this.loading = true
			} else {
				this.widgetLoading = true
			}
			HTTP({
				method: 'GET',
				url: '/statistics?start=' + this.startDate + '&end=' + this.endDate + '&format=' + this.format,
				crossdomain: true
			})
			.then(response => {
				this.analyticsData = []
				this.graphStatusData = [
					['Status', 'Amount']
				]
				this.graphStartedData = [
					['Started', 'Amount']
				]
				this.graphProfitData = [
					['Year', 'Revenue', 'Cost', 'Profit']
				]
				this.analyticsData = response.data
				const events = this.analyticsData.total
				this.graphStatusData.push(['Accepted', events.accepted], ['Rejected', events.rejected], ['Pending', events.pending], ['Duplicate', events.duplicate], ['Paid out', events.paid_out], ['Server error', events.server_error], ['Validation error', events.validation_error])
				this.graphStartedData.push(['Finished', events.accepted + events.rejected + events.pending], ['Unfinished', events.started])
				this.computeGraphProfitData()
				this.loadBounces()
			})
			.catch(error => {
				if (!this.initial) {
					this.loading = false
				} else {
					this.widgetLoading = false
				}
				this.$notify({
					title: 'Error',
					message: error.response.data.status ? error.response.data.status : 'An error occured, please try later',
					type: 'error',
					position: 'bottom-right',
					duration: 3500
				})
			})
		},
		loadBounces () {
			HTTP({
				method: 'GET',
				url: '/bounces?start=' + this.startDate + '&end=' + this.endDate,
				crossdomain: true
			})
			.then(response => {
				this.graphBouncesData = [
					['Step', 'Value']
				]
				this.graphBouncesData.push(['Step 1', response.data.step_1], ['Step 2', response.data.step_2], ['Step 3', response.data.step_3])
				if (!this.initial) {
					this.loading = false
				} else {
					this.widgetLoading = false
				}
				this.initial = true
			})
			.catch(error => {
				if (!this.initial) {
					this.loading = false
				} else {
					this.widgetLoading = false
				}
				this.$notify({
					title: 'Error',
					message: error.response.data.status ? error.response.data.status : 'An error occured, please try later',
					type: 'error',
					position: 'bottom-right',
					duration: 3500
				})
			})
		},
		computeGraphProfitData () {
			this.analyticsData.graph.forEach((item) => {
				if (item) {
					let day = item.day
					let revenue = item.calculations.revenue
					let cost = item.calculations.cost
					let profit = item.calculations.profit
					this.graphProfitData.push([day, revenue, cost, profit])
				}
			})
		},
		formatDate (date) {
			return date.toISOString().split('T')[0]
		},
		submitRange () {
			this.startDate = this.rangeValue[0]
			this.endDate = this.rangeValue[1]
			this.daysOffset()
			this.loadStatistics()
		},
		daysOffset () {
			const oneDay = 24 * 60 * 60 * 1000
			const firstDate = new Date(this.startDate)
			const secondDate = new Date(this.endDate)
			const diffDays = Math.round(Math.abs((firstDate - secondDate) / oneDay))
			if (diffDays < 21) {
				this.format = 'day'
			} else if (diffDays >= 21 && diffDays < 180) {
				this.format = 'week'
			} else if (diffDays >= 180) {
				this.format = 'month'
			}
		},
	}
}
</script>
